import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { ToasterSeverities } from "@enums/toaster-severities";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { ClientService } from "@services/client.service";
import { NotificationService } from "@services/notification.service";
import {
  catchError,
  EMPTY,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import {
  CLIENT_ACTION_FAILURE,
  LOAD_CLIENT,
  REQUEST_CHANGES,
  SAVE_CLIENT,
  SET_CLIENT,
  SET_CLIENT_LOADING,
  UPDATE_PASSWORD,
} from "./client.actions";
import { SELECT_CLIENT_ID } from "./client.selectors";

@Injectable()
export class ClientEffects {
  loadClient$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_CLIENT),
      mergeMap(({ id }): Observable<Action> => {
        this.store.dispatch(SET_CLIENT_LOADING({ loading: true }));

        return this.clientService.getClient$(id).pipe(
          switchMap((data) => {
            this.store.dispatch(SET_CLIENT_LOADING({ loading: false }));

            return of(SET_CLIENT({ data }));
          }),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(SET_CLIENT_LOADING({ loading: false }));
            return of(CLIENT_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  changePassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UPDATE_PASSWORD),
      withLatestFrom(this.store.select(SELECT_CLIENT_ID)),
      mergeMap(([action, id]): Observable<Action> => {
        return this.clientService.updatePassword$(id, action.passwords).pipe(
          switchMap(() => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );
            return EMPTY;
          }),
          catchError((response: HttpErrorResponse) => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error.message
            );
            return EMPTY;
          })
        );
      })
    );
  });

  saveClient$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SAVE_CLIENT),
      withLatestFrom(this.store.select(SELECT_CLIENT_ID)),
      mergeMap(([{ client }, id]): Observable<Action> => {
        this.store.dispatch(SET_CLIENT_LOADING({ loading: true }));

        return this.clientService.saveClient$(client, id).pipe(
          switchMap(() => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );
            this.store.dispatch(SET_CLIENT_LOADING({ loading: false }));

            return of(LOAD_CLIENT({ id }));
          }),
          catchError((response: HttpErrorResponse) => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error.message
            );
            this.store.dispatch(SET_CLIENT_LOADING({ loading: false }));
            return EMPTY;
          })
        );
      })
    );
  });

  requestChanges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(REQUEST_CHANGES),
      withLatestFrom(this.store.select(SELECT_CLIENT_ID)),
      mergeMap(([action, id]): Observable<Action> => {
        return this.clientService.requestChanges$(id, action.data).pipe(
          switchMap(() => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );
            return EMPTY;
          }),
          catchError((response: HttpErrorResponse) => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error.message
            );
            return EMPTY;
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private clientService: ClientService,
    private store: Store,
    private dialog: DialogHelper,
    private notificationService: NotificationService
  ) {}
}
